import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { chartColors } from '../../../Shared/colors';
import { ComposedChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Line, Legend } from 'recharts';
import { useGetDashboardCompleteQuery } from '../../../features/employeeSlice';
import { 
  CallsReportContainer, 
  ChartSkeleton
} from '../../../Shared/ChartComponents';


const monthNames = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
];

// Enhanced Tooltip Component
const EnhancedTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div style={{
        background: 'white',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        padding: '12px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        fontSize: '14px'
      }}>
        <p style={{ 
          margin: '0 0 8px 0', 
          fontWeight: '600', 
          color: '#374151',
          borderBottom: '1px solid #e5e7eb',
          paddingBottom: '4px'
        }}>
          {label}
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ 
              width: '12px', 
              height: '12px', 
              backgroundColor: '#3b82f6', 
              borderRadius: '2px' 
            }}></div>
            <span style={{ color: '#374151' }}>Calls Connected:</span>
            <span style={{ fontWeight: '600', color: '#1f2937' }}>{data.calls}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ 
              width: '12px', 
              height: '12px', 
              backgroundColor: '#10b981', 
              borderRadius: '2px' 
            }}></div>
            <span style={{ color: '#374151' }}>Meetings:</span>
            <span style={{ fontWeight: '600', color: '#1f2937' }}>{data.meetings}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ 
              width: '12px', 
              height: '12px', 
              backgroundColor: '#f59e0b', 
              borderRadius: '2px' 
            }}></div>
            <span style={{ color: '#374151' }}>Conversion Rate:</span>
            <span style={{ fontWeight: '600', color: '#1f2937' }}>{data.callToMeetingRatio}%</span>
          </div>
        </div>
      </div>
    );
  }
  return null;
};

export const CallsReport = ({ staff }) => {
  const { data: dashboardData, isFetching } = useGetDashboardCompleteQuery({ staffId: staff?.id });
  const [callReport, setCallReport] = useState([]);

  // Suppress defaultProps warnings for Recharts components
  useEffect(() => {
    const originalConsoleWarn = console.warn;
    console.warn = (...args) => {
      if (
        args[0] && 
        typeof args[0] === 'string' && 
        (args[0].includes('defaultProps') || args[0].includes('YAxis') || args[0].includes('XAxis'))
      ) {
        return;
      }
      originalConsoleWarn.apply(console, args);
    };

    return () => {
      console.warn = originalConsoleWarn;
    };
  }, []);

  useEffect(() => {
    if (!dashboardData?.callsReport) {
      return;
    }


    const monthData = Array.from({ length: 12 }, (_, index) => ({
      name: monthNames[index],
      calls: 0,
      meetings: 0,
      month: index + 1
    }));

    // Process calls data
    dashboardData.callsReport.forEach((report) => {
      const monthIndex = report.month - 1; 
      if (monthIndex >= 0 && monthIndex < 12) {
        monthData[monthIndex].calls = report.calls;
      }
    });

    // Process meetings data
    if (dashboardData.meetingReport) {
      dashboardData.meetingReport.forEach((report) => {
        const monthIndex = report.month - 1; 
        if (monthIndex >= 0 && monthIndex < 12) {
          monthData[monthIndex].meetings = report.meetings;
        }
      });
    }

    // Calculate call-to-meeting ratio
    const enhancedData = monthData.map(month => ({
      ...month,
      callToMeetingRatio: month.calls > 0 ? 
        parseFloat(((month.meetings / month.calls) * 100).toFixed(1)) : 0
    }));

    setCallReport(enhancedData);
  }, [dashboardData]);

  if (isFetching) {
    return <ChartSkeleton />;
  }

  return (
    <CallsReportContainer>
      <div className="chart-header">
        <div>
          <h3 className="chart-title">Call Performance Report</h3>
          <p className="chart-subtitle">Calls connected, meetings scheduled & conversion rates</p>
        </div>
        <PerformanceMetrics>
          {callReport.length > 0 && (
            <>
              <div className="metric">
                <span className="metric-label">Total Calls:</span>
                <span className="metric-value">
                  {callReport.reduce((sum, month) => sum + month.calls, 0)}
                </span>
              </div>
              <div className="metric">
                <span className="metric-label">Meetings This Year:</span>
                <span className="metric-value">
                  {callReport.reduce((sum, month) => sum + month.meetings, 0)}
                </span>
              </div>
              <div className="metric">
                <span className="metric-label">Avg Conversion:</span>
                <span className="metric-value">
                  {(() => {
                    const totalCalls = callReport.reduce((sum, month) => sum + month.calls, 0);
                    const totalMeetings = callReport.reduce((sum, month) => sum + month.meetings, 0);
                    return totalCalls > 0 ? ((totalMeetings / totalCalls) * 100).toFixed(1) + '%' : '0%';
                  })()}
                </span>
              </div>
            </>
          )}
        </PerformanceMetrics>
      </div>
      
      <div style={{ width: '100%', height: '350px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={callReport}
            margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 20,
            }}
          >
            <defs>
              <linearGradient id="callsGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={chartColors.secondary} stopOpacity={0.3}/>
                <stop offset="95%" stopColor={chartColors.secondary} stopOpacity={0.05}/>
              </linearGradient>
              <linearGradient id="meetingsGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#10b981" stopOpacity={0.05}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis 
              dataKey="name" 
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: '#666' }}
              type="category"
              scale="point"
            />
            <YAxis 
              yAxisId="left"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: '#666' }}
              label={{ value: 'Count', angle: -90, position: 'insideLeft' }}
              type="number"
              domain={[0, 'dataMax']}
            />
            <YAxis 
              yAxisId="right"
              orientation="right"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: '#666' }}
              label={{ value: 'Conversion Rate (%)', angle: 90, position: 'insideRight' }}
              type="number"
              domain={[0, 100]}
            />
            <Tooltip 
              content={<EnhancedTooltip />}
              cursor={{ stroke: chartColors.secondary, strokeWidth: 1, strokeDasharray: '5 5' }}
            />
            <Legend />
            
            {/* Calls Connected Area */}
            <Area 
              yAxisId="left"
              type="monotone" 
              dataKey="calls" 
              name="Calls Connected"
              stroke={chartColors.secondary}
              strokeWidth={3}
              fill="url(#callsGradient)"
              dot={{ fill: chartColors.secondary, strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6, stroke: chartColors.secondary, strokeWidth: 2 }}
              connectNulls={false}
            />
            
            {/* Meetings Scheduled Area */}
            <Area 
              yAxisId="left"
              type="monotone" 
              dataKey="meetings" 
              name="Meetings Scheduled"
              stroke="#10b981"
              strokeWidth={3}
              fill="url(#meetingsGradient)"
              dot={{ fill: "#10b981", strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6, stroke: "#10b981", strokeWidth: 2 }}
              connectNulls={false}
            />
            
            {/* Call-to-Meeting Ratio Line */}
            <Line 
              yAxisId="right"
              type="monotone" 
              dataKey="callToMeetingRatio" 
              name="Conversion Rate (%)"
              stroke="#f59e0b"
              strokeWidth={3}
              dot={{ fill: "#f59e0b", strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6, stroke: "#f59e0b", strokeWidth: 2 }}
              connectNulls={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </CallsReportContainer>
  );
};

// Add custom styles for performance metrics
const PerformanceMetrics = styled.div`
  display: flex;
  gap: 1.5rem;
  margin-top: 1rem;
  
  .metric {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 0.75rem 1rem;
    background: #f8fafc;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
    min-width: 120px;
    
    .metric-label {
      font-size: 0.75rem;
      color: #64748b;
      font-weight: 500;
      text-transform: uppercase;
      letter-spacing: 0.025em;
      margin-bottom: 0.25rem;
    }
    
    .metric-value {
      font-size: 1.25rem;
      font-weight: 700;
      color: #1e293b;
    }
  }
`;

